import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Repeat, Tv, Receipt, Landmark, CalendarClock, ExternalLink, BookOpen, Target } from "lucide-react";
import AppShell from "../components/AppShell";
import { EmptyData, ProGate, Spinner } from "../components/Shared";
import { useAuth } from "../context/AuthContext";
import { api, gbp, catColor, fmtDate } from "../lib/api";

const KIND = {
    subscription: { label: "Subscriptions", icon: Tv, hint: "Streaming, apps, gym — the easiest wins. Cancel anything you haven't used in 30 days." },
    bill: { label: "Bills & insurance", icon: Receipt, hint: "Shop around before renewal; loyalty is rarely rewarded." },
    finance: { label: "Finance & credit", icon: Landmark, hint: "Check APRs and overpay the dearest first." },
    recurring: { label: "Other regular payments", icon: Repeat, hint: "Regular payments we spotted — make sure you recognise them all." },
};

export default function Subscriptions() {
    const { isPro } = useAuth();
    const [data, setData] = useState(null);
    useEffect(() => { if (isPro) api.get("/subscriptions").then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, [isPro]);

    if (!isPro) return <AppShell title="Subscriptions" subtitle="Recurring payments" testid="subscriptions-page"><ProGate feature="Subscription tracker" /></AppShell>;
    if (!data) return <AppShell title="Subscriptions" subtitle="Recurring payments" testid="subscriptions-page"><Spinner /></AppShell>;
    if (data.items.length === 0) return <AppShell title="Subscriptions" subtitle="Recurring payments" testid="subscriptions-page"><EmptyData /></AppShell>;

    const groups = Object.keys(KIND).map((k) => ({ key: k, ...KIND[k], items: data.items.filter((r) => r.kind === k) })).filter((g) => g.items.length);

    return (
        <AppShell title="Subscriptions & bills" subtitle="Recurring payments" testid="subscriptions-page">
            <div className="grid gap-4 sm:grid-cols-3" data-testid="subs-stats">
                <Big label="Recurring every month" value={gbp(data.monthly_total, 0)} testid="subs-monthly" />
                <Big label="Just subscriptions" value={gbp(data.subscriptions_monthly, 0)} sub={`${gbp(data.subscriptions_monthly * 12, 0)} a year`} tone="accent" testid="subs-subscriptions" />
                <Big label="Regular payments found" value={String(data.items.length)} testid="subs-count" />
            </div>
            {groups.map((g, gi) => (
                <section key={g.key} className="mt-6" data-testid={`subs-group-${g.key}`}>
                    <div className="mb-2 flex items-center gap-2"><g.icon className="h-4 w-4 text-primary" /><h3 className="font-heading text-base font-bold text-foreground">{g.label}</h3><span className="text-xs text-muted-foreground">· {g.hint}</span></div>
                    <div className="overflow-hidden rounded-2xl border border-border bg-card">
                        <ul className="divide-y divide-border">
                            {g.items.map((r, i) => (
                                <motion.li key={r.merchant} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: (gi * 4 + i) * 0.03 }}
                                    className="flex items-center gap-3 px-4 py-3" data-testid={`sub-row-${g.key}-${i}`}>
                                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: catColor(r.category) }} />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-foreground">{r.merchant}</p>
                                        <p className="flex items-center gap-1 text-xs text-muted-foreground"><CalendarClock className="h-3 w-3" /> {r.occurrences}× · last {fmtDate(r.last_date)} · {r.category}</p>
                                        {r.switch && <SwitchLinks item={r} idx={`${g.key}-${i}`} compact />}
                                    </div>
                                    <div className="text-right">
                                        <p className="font-mono text-sm font-semibold text-foreground">{gbp(r.monthly)}<span className="text-xs text-muted-foreground">/mo</span></p>
                                        <p className="font-mono text-[11px] text-muted-foreground">{gbp(r.annual, 0)}/yr</p>
                                    </div>
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                </section>
            ))}
        </AppShell>
    );
}

export function SwitchLinks({ item, idx, compact }) {
    const s = item.switch;
    const target = item.target_monthly ?? Math.round(item.monthly * 0.75 * 100) / 100;
    const current = item.current_monthly ?? item.monthly;
    return (
        <div className={`flex flex-wrap items-center gap-2 ${compact ? "mt-1.5" : "mt-3"}`} data-testid={`switch-links-${idx}`}>
            <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`switch-compare-${idx}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
                <ExternalLink className="h-3.5 w-3.5" /> Compare {s.product} on {s.provider}
            </a>
            <span className="inline-flex items-center gap-1 rounded-full border border-accent/30 bg-accent/10 px-2.5 py-1 font-mono text-[11px] text-accent" data-testid={`switch-target-${idx}`} title="Aim to beat this price">
                <Target className="h-3 w-3" /> Beat £{Number(current).toFixed(2)} → target £{Number(target).toFixed(2)}/mo
            </span>
            <a href={s.guide_url} target="_blank" rel="noopener noreferrer" data-testid={`switch-guide-${idx}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                <BookOpen className="h-3.5 w-3.5" /> MSE guide
            </a>
        </div>
    );
}

function Big({ label, value, sub, tone, testid }) {
    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className={`mt-1 font-heading text-3xl font-black tracking-tight ${tone === "accent" ? "text-accent" : "text-foreground"}`}>{value}</p>
            {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
        </div>
    );
}
